import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { RaiseDiagnosticsDialog } from '@/components/diagnostics/RaiseDiagnosticsDialog';
import { formatDateTime } from '@/lib/format';
import {
  useClearDiagnostics,
  useDiagnosticOverrides,
  type DiagnosticOverride,
} from './diagnostics/useDiagnostics';

/**
 * Runtime diagnostics (M1-34): what is switched on right now, across the whole
 * platform, and the controls to raise and clear it.
 *
 * The list is every scope, not just account ones — that platform-wide view is
 * the thing the tenant-facing endpoint deliberately refuses to give anyone
 * (M0-32), and it is the reason this screen is behind a `staff:` permission.
 *
 * ⚠️ An account id in `?account=` is an **identifier**, which the telemetry rule
 * allows — unlike the tenant search term M1-33 kept out of the URL. It arrives
 * from the link on a tenant's page, so a support agent who has found the tenant
 * does not then have to copy a GUID by hand.
 */
export function Diagnostics() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const prefilledAccount = searchParams.get('account') ?? undefined;

  const overrides = useDiagnosticOverrides();
  const clear = useClearDiagnostics();

  const [showRaise, setShowRaise] = useState(prefilledAccount !== undefined);
  const [clearing, setClearing] = useState<DiagnosticOverride | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const rows = overrides.data ?? [];

  function closeRaise() {
    setShowRaise(false);
    // Spent, not merely read: leaving `?account=` in the address bar would
    // reopen the dialog on every back-navigation to this screen.
    if (prefilledAccount !== undefined) {
      const next = new URLSearchParams(searchParams);
      next.delete('account');
      setSearchParams(next, { replace: true });
    }
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('diagnostics.title')}</h1>
          <p className="max-w-prose text-sm text-[var(--color-muted-foreground)]">
            {t('diagnostics.subtitle')}
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setShowRaise(true)}>
          {t('diagnostics.raise.action')}
        </Button>
      </div>

      {overrides.isPending ? (
        <LoadingState label={t('diagnostics.loading')} />
      ) : overrides.isError ? (
        <ErrorState
          title={t('diagnostics.loadErrorTitle')}
          description={t('diagnostics.loadErrorDescription')}
          onRetry={() => void overrides.refetch()}
        />
      ) : rows.length === 0 ? (
        // Nothing switched on is the normal, healthy state — it should read as
        // reassurance, not as a screen that failed to load.
        <EmptyState
          title={t('diagnostics.emptyTitle')}
          description={t('diagnostics.emptyDescription')}
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <caption className="sr-only">{t('diagnostics.tableCaption')}</caption>
            <thead className="border-b bg-[var(--color-muted)]">
              <tr>
                <th scope="col" className="px-4 py-2 text-left font-medium">
                  {t('diagnostics.columnScope')}
                </th>
                <th scope="col" className="px-4 py-2 text-left font-medium">
                  {t('diagnostics.columnLevel')}
                </th>
                <th scope="col" className="px-4 py-2 text-left font-medium">
                  {t('diagnostics.columnExpires')}
                </th>
                <th scope="col" className="hidden px-4 py-2 text-left font-medium md:table-cell">
                  {t('diagnostics.columnReason')}
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  <span className="sr-only">{t('diagnostics.columnActions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b last:border-b-0">
                  <td className="px-4 py-2">
                    <span className="font-medium">
                      {t(`diagnostics.scope.${row.scope}`, { defaultValue: row.scope })}
                    </span>
                    {row.target ? (
                      <span className="block font-mono text-xs break-all text-[var(--color-muted-foreground)]">
                        {row.target}
                      </span>
                    ) : null}
                    {/* The reason column is hidden on narrow screens, so keep it
                        reachable rather than merely invisible — an override
                        nobody can explain is one nobody dares switch off. */}
                    <span className="block text-xs text-[var(--color-muted-foreground)] md:hidden">
                      {row.reason}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {t(`diagnostics.level.${row.level}`, { defaultValue: row.level })}
                  </td>
                  <td className="px-4 py-2">
                    <span>{remainingLabel(row, t)}</span>
                    {/* The absolute time as well, on the viewer's clock with its
                        own time — one formatter, one clock (M1-04). The relative
                        figure leads because "when does this stop" is the actual
                        question (M0-32). */}
                    <span className="block text-xs text-[var(--color-muted-foreground)]">
                      {formatDateTime(row.expiresAt)}
                    </span>
                  </td>
                  <td className="hidden max-w-xs px-4 py-2 text-[var(--color-muted-foreground)] md:table-cell">
                    {row.reason}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setClearing(row)}
                    >
                      {t('diagnostics.clear.action')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p
        role="status"
        aria-live="polite"
        className="sr-only"
        data-testid="diagnostics-announcement"
      >
        {announcement}
      </p>

      {showRaise ? (
        <RaiseDiagnosticsDialog
          initialTarget={prefilledAccount}
          onClose={closeRaise}
          onRaised={(scope, target) =>
            // The page owns the live region, so the page does the `t()` — the
            // dialog handed up raw facts (M1-30).
            setAnnouncement(
              target
                ? t('diagnostics.raise.announcedWithTarget', {
                    scope: t(`diagnostics.scope.${scope}`),
                    target,
                  })
                : t('diagnostics.raise.announced', { scope: t(`diagnostics.scope.${scope}`) }),
            )
          }
        />
      ) : null}

      {clearing ? (
        <ConfirmDialog
          titleId="clear-diagnostics-title"
          title={t('diagnostics.clear.title')}
          description={t('diagnostics.clear.description', {
            scope: t(`diagnostics.scope.${clearing.scope}`, { defaultValue: clearing.scope }),
            target: clearing.target ?? t('diagnostics.everything'),
          })}
          confirmLabel={t('diagnostics.clear.confirm')}
          isPending={clear.isPending}
          errorMessage={clear.isError ? t('diagnostics.clear.failed') : undefined}
          onConfirm={() =>
            clear.mutate(clearing.id, {
              onSuccess: () => {
                setAnnouncement(
                  t('diagnostics.clear.announced', {
                    scope: t(`diagnostics.scope.${clearing.scope}`, {
                      defaultValue: clearing.scope,
                    }),
                  }),
                );
                setClearing(null);
                clear.reset();
              },
            })
          }
          onClose={() => {
            setClearing(null);
            // Or the next confirmation opens already showing the last failure.
            clear.reset();
          }}
          testId="clear-diagnostics-dialog"
        />
      ) : null}
    </section>
  );
}

/**
 * "2h 14m left".
 *
 * Built from the server's own `minutesRemaining` rather than from `expiresAt`
 * and the browser clock: the value is computed server-side at response time and
 * refreshed each minute (see `useDiagnosticOverrides`), so a browser whose clock
 * is wrong cannot make an override look longer-lived than it is.
 */
function remainingLabel(
  row: DiagnosticOverride,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const total = Math.max(0, Math.floor(Number(row.minutesRemaining)));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;

  return hours > 0
    ? t('diagnostics.remainingHours', { hours, minutes })
    : t('diagnostics.remainingMinutes', { minutes });
}
